// Schemi Zod per il pannello configurazione self-service.
// Single source of truth per backend Fastify schema + admin form validation.
import { z } from 'zod';
// ============================================================================
// Branding (Sprint 5)
// ============================================================================
export const BrandingUpdateSchema = z.object({
    brand_logo_url: z.string().url().nullable().optional(),
    brand_primary_color: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/u, 'Colore deve essere in formato esadecimale, es. #2A8478')
        .nullable()
        .optional(),
    brand_secondary_color: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/u)
        .nullable()
        .optional(),
    brand_font_override: z.string().max(100).nullable().optional(),
});
// ============================================================================
// Identità organizzazione
// ============================================================================
export const OrganizationUpdateSchema = z.object({
    organization_name: z.string().max(200).nullable().optional(),
    organization_legal_name: z.string().max(300).nullable().optional(),
    organization_vat_or_id: z.string().max(50).nullable().optional(),
    organization_contact_email: z.string().email().nullable().optional().or(z.literal('')),
    organization_contact_phone: z.string().max(50).nullable().optional(),
    organization_address: z.string().max(500).nullable().optional(),
    display_name: z.string().min(1).max(200).optional(), // mantiene alias retro-compat
});
// ============================================================================
// LLM
// ============================================================================
export const LlmModeEnum = z.enum(['managed', 'client']);
export const LlmUpdateSchema = z.object({
    mode: LlmModeEnum.optional(),
    model: z.string().min(1).optional(),
    temperature: z.number().min(0).max(1).optional(),
    max_tokens: z.number().int().min(64).max(8192).optional(),
});
export const LlmClientKeySetSchema = z.object({
    api_key: z
        .string()
        .min(20, 'API key troppo corta')
        .max(500, 'API key troppo lunga')
        .refine((v) => v.startsWith('sk-ant-'), {
        message: 'Le API key Anthropic iniziano con "sk-ant-"',
    }),
});
// ============================================================================
// Voice (Sprint 8) — STT Deepgram + TTS ElevenLabs
// ============================================================================
export const VoiceProviderModeEnum = z.enum(['managed', 'client']);
export const SttProviderEnum = z.enum(['deepgram']); // futuro: 'whisper' | 'azure'
export const TtsProviderEnum = z.enum(['elevenlabs']); // futuro
/**
 * Configurazione voce shape canonica salvata in installation_settings.voice_config (JSONB).
 * `enabled=false` per default; quando true, l'admin DEVE aver popolato voice_id TTS.
 */
export const VoiceConfigSchema = z.object({
    enabled: z.boolean().default(false),
    stt: z
        .object({
        provider: SttProviderEnum.default('deepgram'),
        mode: VoiceProviderModeEnum.default('managed'),
        model: z.string().min(1).default('nova-3'),
    })
        .default({}),
    tts: z
        .object({
        provider: TtsProviderEnum.default('elevenlabs'),
        mode: VoiceProviderModeEnum.default('managed'),
        voice_id: z.string().min(1).optional(),
        model: z.string().min(1).default('eleven_multilingual_v2'),
    })
        .default({}),
    limits: z
        .object({
        monthly_audio_minutes_soft: z.number().int().min(1).max(1_000_000).optional(),
        monthly_audio_minutes_hard: z.number().int().min(1).max(1_000_000).optional(),
    })
        .default({}),
});
/**
 * PATCH /api/admin/settings/voice — accetta partial update (deep merge nel handler).
 */
export const VoiceUpdateSchema = z
    .object({
    enabled: z.boolean().optional(),
    stt: z
        .object({
        provider: SttProviderEnum.optional(),
        mode: VoiceProviderModeEnum.optional(),
        model: z.string().min(1).optional(),
    })
        .optional(),
    tts: z
        .object({
        provider: TtsProviderEnum.optional(),
        mode: VoiceProviderModeEnum.optional(),
        voice_id: z.string().min(1).nullable().optional(),
        model: z.string().min(1).optional(),
    })
        .optional(),
    limits: z
        .object({
        monthly_audio_minutes_soft: z.number().int().min(0).max(1_000_000).nullable().optional(),
        monthly_audio_minutes_hard: z.number().int().min(0).max(1_000_000).nullable().optional(),
    })
        .optional(),
})
    .refine((v) => !v.limits?.monthly_audio_minutes_soft ||
    !v.limits.monthly_audio_minutes_hard ||
    v.limits.monthly_audio_minutes_soft <= v.limits.monthly_audio_minutes_hard, { message: 'Soft cap deve essere ≤ hard cap', path: ['limits', 'monthly_audio_minutes_soft'] });
/**
 * PUT /api/admin/settings/voice/stt-client-key — set Deepgram client key.
 * Min 20 char è genericissimo (Deepgram API key sono ~40 char alphanumerici, no prefix univoco).
 */
export const VoiceSttClientKeySetSchema = z.object({
    api_key: z.string().min(20, 'API key troppo corta').max(500, 'API key troppo lunga'),
});
/**
 * PUT /api/admin/settings/voice/tts-client-key — set ElevenLabs client key.
 * Min 20 char idem.
 */
export const VoiceTtsClientKeySetSchema = z.object({
    api_key: z.string().min(20, 'API key troppo corta').max(500, 'API key troppo lunga'),
});
/**
 * Voice listVoices response (admin pulisce a sub-set di campi necessari UI).
 */
export const VoiceListItemSchema = z.object({
    voice_id: z.string(),
    name: z.string(),
    preview_url: z.string().url().nullable().optional(),
    labels: z.record(z.string()).nullable().optional(),
    language: z.string().nullable().optional(),
});
// ============================================================================
// Limiti consumo
// ============================================================================
export const LimitsUpdateSchema = z
    .object({
    monthly_soft: z.number().int().min(1).max(1_000_000),
    monthly_hard: z.number().int().min(1).max(1_000_000),
    alert_email_recipient: z.string().email().nullable().optional().or(z.literal('')),
})
    .refine((v) => v.monthly_soft <= v.monthly_hard, {
    message: 'Il limite soft deve essere ≤ del limite hard',
    path: ['monthly_soft'],
});
// ============================================================================
// Retention
// ============================================================================
export const RetentionUpdateSchema = z.object({
    retention_days: z.number().int().min(7).max(365),
});
// ============================================================================
// SMTP
// ============================================================================
export const SmtpUpdateSchema = z.object({
    host: z.string().min(1).max(255),
    port: z.number().int().min(1).max(65535),
    user: z.string().min(1).max(255),
    // password: opzionale al PATCH (se omessa = mantieni esistente cifrata)
    password: z.string().min(1).max(500).optional(),
    from: z.string().min(3).max(300),
    secure: z.boolean(),
});
// ============================================================================
// Privacy
// ============================================================================
export const PrivacyUpdateSchema = z.object({
    privacy_policy_url: z.string().url().nullable().optional().or(z.literal('')),
    privacy_dpo_email: z.string().email().nullable().optional().or(z.literal('')),
});
// ============================================================================
// Admin user management
// ============================================================================
export const AdminUserCreateSchema = z.object({
    email: z.string().email().toLowerCase(),
    role: z.enum(['master', 'operator', 'viewer']),
    send_invite_email: z.boolean().default(false),
});
// ============================================================================
// 2FA TOTP
// ============================================================================
export const TotpVerifySchema = z.object({
    code: z
        .string()
        .min(6)
        .max(20) // recovery code 9 char con trattino, lasciamo margine
        .regex(/^[A-Z0-9-]{6,20}$/u, 'Codice non valido'),
    is_recovery_code: z.boolean().optional(),
});
export const TotpSetupVerifySchema = z.object({
    code: z.string().regex(/^\d{6}$/u, 'Inserisci 6 cifre'),
});
export const TotpDisableSchema = z.object({
    current_code: z.string().min(6).max(20),
});
export const PasswordChangeSchema = z
    .object({
    current_password: z.string().min(1),
    new_password: z.string().min(12).max(200),
    confirm_password: z.string().min(12).max(200),
})
    .refine((v) => v.new_password === v.confirm_password, {
    message: 'La conferma password non corrisponde',
    path: ['confirm_password'],
});
// ============================================================================
// Login flow esteso
// ============================================================================
/**
 * Risposta login. Tre rami:
 *  - { ok: true, user } → login completato (no 2FA)
 *  - { ok: true, requires_2fa: true, partial_token } → richiede codice TOTP
 *  - { ok: true, requires_2fa_setup: true, partial_token } → master in grace, deve configurare
 */
export const LoginResponseSchema = z.discriminatedUnion('flow', [
    z.object({
        ok: z.literal(true),
        flow: z.literal('complete'),
        user: z.object({
            id: z.string().uuid(),
            email: z.string().email(),
            role: z.enum(['master', 'operator', 'viewer']),
            must_change_password: z.boolean().optional(),
        }),
    }),
    z.object({
        ok: z.literal(true),
        flow: z.literal('require_2fa'),
        partial_token: z.string(),
    }),
    z.object({
        ok: z.literal(true),
        flow: z.literal('require_2fa_setup'),
        partial_token: z.string(),
    }),
]);
export const Verify2faRequestSchema = z.object({
    partial_token: z.string().min(10),
    code: z.string().min(6).max(20),
    is_recovery_code: z.boolean().optional(),
});
//# sourceMappingURL=settings.js.map